import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { useCallback, useContext, useEffect } from "react";

// Icons
import { XMarkIcon, ArrowDownOnSquareIcon, UserPlusIcon } from "@heroicons/react/24/outline";

// Contexts
import LoaderContext from "../../../../context/psikotest/LoaderContext";
import SnackbarContext from "../../../../context/psikotest/SnackbarContext";

// Helpers
import errorHandler from "../../../../helper/psikotest/errorHandler";

// Controller
import AssessmentParticipant from "../../../../controller/psikotest/psikotest_system/assessment_participant";

const schema = yup.object().shape({
    id: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(),
    assessment_id: yup
        .number()
        .label("Assessment ID")
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(),
    full_name: yup
        .string()
        .label("Full Name")
        .transform((value) => (value === null ? "" : value))
        .required(),
    email: yup
        .string()
        .email()
        .label("Email")
        .transform((value) => (value === null ? "" : value))
        .required(),
});

function ModalParticipant(
    props: {
        data?: TPSAssessmentParticipant,
        assessment_id: number,
        handleClose: () => void
    }) {
    // contexts
    const { setMessage } = useContext(LoaderContext);
    const { setNotif } = useContext(SnackbarContext);
    // is create
    const isCreate = props.data === undefined;
    // react hook form
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm<TPSAssessmentParticipant>({ resolver: yupResolver(schema) });

    const getDoc = useCallback(
        async function getDoc() {
            try {
                // fetch document
                if (isCreate) {
                    setValue("id", 0);
                    // update assessment_id
                    setValue("assessment_id", props.assessment_id);
                } else {
                    setValue("id", props.data?.id ?? 0);
                    setValue("assessment_id", props.data?.assessment_id ?? 0);
                    setValue("full_name", props.data?.full_name ?? "");
                    setValue("email", props.data?.email ?? "");
                    setValue("date_of_birth", props.data?.date_of_birth ?? "");
                }
                setMessage("");
            } catch (error) {
                setMessage("");
                const errorMessage = errorHandler(error);
                setNotif({ type: "error", message: errorMessage });
            }
        },
        [setValue, isCreate, setNotif, setMessage, props.assessment_id, props.data]
    );

    useEffect(() => {
        getDoc();
    }, [getDoc]);

    const onSubmit: SubmitHandler<TPSAssessmentParticipant> = async (data) => {
        try {
            setMessage("Save Participant");
            const cacs = new AssessmentParticipant();
            let res
            if (isCreate) {
                res = await cacs.create(data);
            } else {
                res = await cacs.update(data);
            }
            setNotif({ type: "success", message: res.data.message });
            setMessage("");
            props.handleClose();
        } catch (error) {
            setMessage("");
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        }
    };

    return <div>
        <div className="fixed z-20 top-0 left-0 w-screen h-screen bg-black opacity-20"></div>
        <div className="fixed z-30 top-0 left-0 w-screen h-screen bg-transparent flex items-center justify-center">
            <div className="bg-white w-screen md:w-[50%] h-auto md:rounded overflow-auto">
                <div className="p-4 bg-green-800 md:rounded-t text-white flex justify-between">
                    <div className="flex space-x-2">
                        <UserPlusIcon className="w-4" />
                        <h3>Form Participant</h3>
                    </div>
                    <XMarkIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent cursor-pointer" aria-hidden="true" onClick={() => props.handleClose()} />
                </div>
                <div className="p-4 flex flex-col">
                    <div>
                        <form className="w-full">
                            <div className="flex flex-wrap -mx-3">
                                {/* ID */}
                                <input {...register("id")} type="number" className="hidden" />
                                {errors.id && <p className="text-red-500 text-xs italic">{errors.id.message}</p>}
                                {/* Assessment ID */}
                                <input {...register("assessment_id")} type="number" className="hidden" />
                                {errors.assessment_id && <p className="text-red-500 text-xs italic">{errors.assessment_id.message}</p>}
                                {/* Full Name */}
                                <div className="w-full px-3 mb-6">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Full Name
                                    </label>
                                    <div>
                                        <input
                                            className={
                                                (errors?.full_name ? "border-red-500" : "border-gray-200 focus:border-gray-500") +
                                                " first-letter:appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-2 px-3 focus:outline-none focus:bg-white disabled:text-gray-500"
                                            }
                                            {...register("full_name")}
                                        />
                                        {errors.full_name && <p className="text-red-500 text-xs italic">{errors.full_name.message}</p>}
                                    </div>
                                </div>
                                {/* Email */}
                                <div className="w-full px-3 mb-6">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Email
                                    </label>
                                    <div>
                                        <input
                                            type="email"
                                            className={
                                                (errors?.email ? "border-red-500" : "border-gray-200 focus:border-gray-500") +
                                                " first-letter:appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-2 px-3 focus:outline-none focus:bg-white disabled:text-gray-500"
                                            }
                                            {...register("email")}
                                        />
                                        {errors.email && <p className="text-red-500 text-xs italic">{errors.email.message}</p>}
                                    </div>
                                </div>
                                {/* Date of Birth */}
                                <div className="w-full px-3 mb-6">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Date of Birth
                                    </label>
                                    <div>
                                        <input
                                            type={"date"}
                                            className={
                                                (errors?.date_of_birth ? "border-red-500" : "border-gray-200 focus:border-gray-500") +
                                                " first-letter:appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-2 px-3 focus:outline-none focus:bg-white disabled:text-gray-500"
                                            }
                                            {...register("date_of_birth")}
                                        />
                                        {errors.date_of_birth && <p className="text-red-500 text-xs italic">{errors.date_of_birth.message}</p>}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="flex mt-auto">
                        <button
                            onClick={handleSubmit(onSubmit)}
                            className="ml-auto block w-auto px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
                        >
                            <div className="flex">
                                <ArrowDownOnSquareIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
                                Save
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
}

export default ModalParticipant;